import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { BusinessCard } from "@/components/Directory";
import { Footer } from "@/components/Footer";

const EASE = [0.16, 1, 0.3, 1];

export default function SectorPage() {
  const { name } = useParams();
  const sector = decodeURIComponent(name);
  const [businesses, setBusinesses] = useState(null);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
    setBusinesses(null);
    api.get("/businesses", { params: { sector } })
      .then((res) => setBusinesses(res.data))
      .catch(() => setBusinesses([]));
  }, [sector]);

  return (
    <div className="min-h-screen bg-ivory" data-testid="sector-page">
      <header className="fixed top-0 inset-x-0 z-50 border-b border-stone_warm bg-ivory/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
          <Link to="/" className="flex items-center gap-2.5" data-testid="sector-logo-link">
            <span className="inline-block h-2 w-2 rotate-45 bg-gold" aria-hidden="true" />
            <span className="font-serif text-xl font-semibold tracking-tight text-charcoal">Joseph Guild</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-charcoal/60 transition-colors hover:text-gold-dark"
            data-testid="sector-back-link"
          >
            <ArrowLeft size={13} /> Full directory
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 pb-28 pt-32 lg:px-12 lg:pt-40">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-gold-dark"
        >
          Guild sector
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="font-serif text-4xl font-light tracking-tighter text-charcoal sm:text-5xl lg:text-6xl"
          data-testid="sector-title"
        >
          {sector}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.25 }}
          className="mt-4 max-w-lg text-base text-charcoal/65"
          data-testid="sector-count"
        >
          {businesses === null
            ? "Gathering businesses…"
            : businesses.length === 0
              ? "No businesses in this sector yet — the first could be yours."
              : `${businesses.length} ${businesses.length === 1 ? "business" : "businesses"} of the Guild in this sector.`}
        </motion.p>

        {businesses !== null && businesses.length === 0 && (
          <div className="mt-16 border border-dashed border-stone_warm bg-white/60 py-20 text-center" data-testid="sector-empty">
            <p className="font-serif text-2xl font-light italic text-charcoal/50">This sector is waiting for its first member.</p>
            <Link to="/" className="mt-5 inline-block font-mono text-[11px] uppercase tracking-[0.22em] text-gold-dark underline-offset-4 hover:underline" data-testid="sector-empty-register">
              Register a business
            </Link>
          </div>
        )}

        {businesses && businesses.length > 0 && (
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="sector-grid">
            {businesses.map((b, i) => <BusinessCard key={b.id} b={b} index={i} />)}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
